import React, { useState, useEffect, useRef } from 'react';
import TimePicker from '../timePickerComponent/timePickerComponent';
import { AuthContext, MyContext } from '../context/AuthContext';
import { Box, Button, Tooltip, Snackbar, Alert, Typography } from '@mui/material';
import { Stack } from '@mui/material';
import { DateTime } from 'luxon';
import { TimeSelection } from '../model/types';
import { CacheContext, CacheContextType } from '../context/CacheContext';
import { supabase } from '../supabaseClient';
import PermanenceIcon from '../resource/PermanenceIcon';
import { discountedWalletCost, getPowerHourStatus } from '../lib/powerHour';
import { computeStreak, getMilestoneCrossed } from '../lib/streaks';
import { hapticSuccess, hapticHeavy, hapticWarning, hapticError } from '../lib/haptics';
import { playPostSent, playMilestone, playError } from '../lib/sounds';

interface PollDraft {
  question: string;
  pollType: 'standard' | 'survival' | 'race';
  timePerVote: number;  // seconds
  raceGoalSeconds: number;
  options: string[];    // 2-4 labels
}

function SubmitWithTimeButton({
  objectType,
  value,
  parentID,
  parentType,
  onSubmit,
  mentions,
  feedTags,
  pollDraft,
  openAtISO,
}: {
  objectType: string;
  value: string;
  parentID?: string;
  parentType?: string;
  mentions?: Array<any>;
  feedTags?: Array<any>;
  pollDraft?: PollDraft;
  /** Time capsule: if set, post is buried until this ISO moment. Lifespan starts AFTER opening. */
  openAtISO?: string | null;
  onSubmit: () => void;
}) {
  const creationType = objectType.slice(0, -1);
  const userRecord = React.useContext<MyContext>(AuthContext).userRecord;
  const getFeedFromID =
    React.useContext<CacheContextType>(CacheContext).getFeedFromID;
  const allPosts =
    React.useContext<CacheContextType>(CacheContext).allPosts;
  const [showClock, setShowClock] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const ref = React.useRef(null);
  const [time, setTime] = useState<TimeSelection>({
    days: 0,
    hours: 1,
    minutes: 0,
  });

  // Quick-submit via Cmd/Ctrl+Enter fired from the composer wrapper. We
  // route through a window event instead of passing a ref upward — this
  // keeps the composer unaware of our internal submit signature.
  const latestHandleSubmitRef = useRef<(isPermanent?: boolean) => void>(() => undefined);
  // Only top-level posts (not replies, which are rendered inside threads)
  // listen — we avoid double-firing when multiple buttons are mounted.
  useEffect(() => {
    if (parentID) return;
    const onQuickSubmit = () => {
      if (!value?.trim()) return;
      latestHandleSubmitRef.current(false);
    };
    window.addEventListener('wipe-composer-quick-submit', onQuickSubmit);
    return () => window.removeEventListener('wipe-composer-quick-submit', onQuickSubmit);
  }, [parentID, value]);

  // Keep the quick-submit ref aimed at the current closure so the keyboard
  // shortcut always sees the latest `value` / `time` / `pollDraft`.
  useEffect(() => {
    latestHandleSubmitRef.current = (isPermanent?: boolean) => handleSubmit(isPermanent);
  });

  const handleSubmit = async (isPermanent: boolean = false) => {
    if (!userRecord) {
      console.error('User not authenticated');
      return;
    }

    const timeValue =
      time.days * 24 * 60 * 60 + time.hours * 60 * 60 + time.minutes * 60;

    // Power Hour: post still lives for the full timeValue, but wallet
    // deduction is halved while active. This is the core viral loop —
    // people want to post DURING Power Hour to double their time.
    const walletCost = isPermanent ? 0 : discountedWalletCost(timeValue);
    const powerHourActive = getPowerHourStatus().isActive;

    // Time capsule: if buried, expire_at is open_at + timeValue (lifespan
    // starts AFTER the post opens). Permanence wins if both set.
    const buryMoment = openAtISO ? DateTime.fromISO(openAtISO) : null;
    const expireAt = isPermanent
      ? DateTime.fromSeconds(99999999999).toISO()
      : buryMoment
        ? buryMoment.plus({ seconds: timeValue }).toISO()
        : DateTime.now().plus({ seconds: timeValue }).toISO();

    if (isPermanent && userRecord.permanence_tokens < 1) {
      setErrorMsg('Not enough permanence tokens');
      hapticWarning();
      return;
    } else if (walletCost > userRecord.time_wallet_seconds) {
      setErrorMsg('Not enough time in your wallet');
      hapticWarning();
      return;
    }

    try {
      // 1. Create the post or follow. For time capsules, open_at is set
      // and the post will be invisible to non-authors until then (via RLS).
      const insertRow: any = {
        user_record_id: userRecord.id,
        value,
        expire_at: expireAt,
        time_value: isPermanent ? null : timeValue,
        is_permanent: isPermanent,
        parent_id: parentID ?? null,
        parent_type: parentType ?? null,
        is_expired: false,
        is_test_mode: false,
      };
      if (openAtISO && objectType === 'posts' && !parentID) {
        insertRow.open_at = openAtISO;
      }
      const { data: newObject, error: insertError } = await supabase
        .from(objectType)
        .insert(insertRow)
        .select()
        .single();

      if (insertError) throw insertError;

      // 2. Insert poll + options if a poll was attached
      if (pollDraft && objectType === 'posts' && pollDraft.options.filter(Boolean).length >= 2) {
        const { data: poll, error: pollError } = await supabase.from('polls').insert({
          post_id: newObject.id,
          question: pollDraft.question,
          poll_type: pollDraft.pollType,
          time_per_vote: pollDraft.timePerVote,
          race_goal_seconds: pollDraft.raceGoalSeconds,
          expire_at: expireAt,
          is_expired: false,
        }).select().single();
        if (!pollError && poll) {
          const validOptions = pollDraft.options.filter(Boolean);
          await supabase.from('poll_options').insert(
            validOptions.map((label, i) => ({
              poll_id: poll.id,
              label,
              position: i,
              time_total_seconds: 0,
            }))
          );
        }
      }

      // 3. Insert mentions (DB trigger creates notifications)
      if (mentions && mentions.length > 0) {
        const { error: mentionError } = await supabase.from('mentions').insert(
          mentions.map((m) => ({
            user_record_id: userRecord.id,
            mentioned_user_record_id: m.id,
            object_type: objectType,
            object_id: newObject.id,
          })),
        );
        if (mentionError) console.error('Mention insert error:', mentionError);
      }

      // 3. Upsert feeds and insert feed_tags
      if (feedTags && feedTags.length > 0) {
        for (const feed of feedTags) {
          const feedTag = feed.display.replace('#', '');

          const { data: feedRecord, error: feedError } = await supabase
            .from('feeds')
            .upsert(
              { feed_tag: feedTag, expire_at: expireAt },
              { onConflict: 'feed_tag' },
            )
            .select()
            .single();

          if (feedError) {
            console.error('Feed upsert error:', feedError);
            continue;
          }

          await supabase.from('feed_tags').insert({
            feed_id: feedRecord.id,
            user_record_id: userRecord.id,
            object_type: objectType,
            object_id: newObject.id,
          });

          // Extend feed's expire_at if this object lives longer
          const existingFeed = getFeedFromID(feedRecord.id);
          if (existingFeed && existingFeed.expire_at) {
            const existingExpirySecs = DateTime.fromISO(existingFeed.expire_at).toSeconds();
            const newExpirySecs = DateTime.fromISO(expireAt!).toSeconds();
            if (newExpirySecs > existingExpirySecs) {
              await supabase
                .from('feeds')
                .update({ expire_at: expireAt })
                .eq('id', feedRecord.id);
            }
          }
        }
      }

      // 4. Streak milestone check (only for top-level posts, not replies)
      // Compute what the streak was before vs what it is now, including
      // the newly-inserted post. If we crossed a milestone, add the
      // bonus to the wallet in the same update.
      let streakBonus = 0;
      let milestoneLabel: string | null = null;
      if (objectType === 'posts' && !parentID) {
        const existingPosts = allPosts();
        const prevStreak = computeStreak(existingPosts, userRecord.id);
        const newStreak = computeStreak(
          [...existingPosts, newObject as any],
          userRecord.id,
        );
        const milestone = getMilestoneCrossed(prevStreak, newStreak);
        if (milestone) {
          streakBonus = milestone.bonusSeconds;
          milestoneLabel = milestone.label;
        }
      }

      // 5. Deduct time wallet (and permanence token if applicable)
      // During Power Hour, walletCost is half of timeValue — post still
      // lives for the full timeValue, but your wallet only pays half.
      // Streak milestone bonus (if any) is added here in the same update.
      const { error: walletError } = await supabase
        .from('users')
        .update({
          time_wallet_seconds: isPermanent
            ? userRecord.time_wallet_seconds + streakBonus
            : userRecord.time_wallet_seconds - walletCost + streakBonus,
          permanence_tokens: isPermanent
            ? userRecord.permanence_tokens - 1
            : userRecord.permanence_tokens,
        })
        .eq('id', userRecord.id);

      if (walletError) throw walletError;

      // Fire a floating time-delta indicator. Include streak bonus
      // so the user sees the net change.
      {
        const delta = (isPermanent ? 0 : -walletCost) + streakBonus;
        if (delta !== 0) {
          window.dispatchEvent(new CustomEvent('wipe-time-delta', { detail: { seconds: delta } }));
        }
      }

      setShowClock(false);
      setSuccessMsg(
        buryMoment
          ? `🔒 Buried! Your post will open ${buryMoment.toRelative() ?? 'later'}.`
          : milestoneLabel
            ? milestoneLabel
            : isPermanent
              ? `Immortalized! Your ${creationType} will live forever.`
              : powerHourActive
                ? `⚡ ${creationType.charAt(0).toUpperCase() + creationType.slice(1)} submitted at 50% OFF!`
                : `${creationType.charAt(0).toUpperCase() + creationType.slice(1)} submitted!`,
      );
      // Heavier thump for permanence / milestones; regular tick otherwise.
      if (isPermanent || milestoneLabel) {
        hapticHeavy();
        playMilestone();
      } else {
        hapticSuccess();
        playPostSent();
      }

      // First-post celebration: only fires once per browser, the dialog
      // listener handles its own no-op on subsequent triggers. We fire
      // for top-level posts only — replies and follows don't count.
      if (objectType === 'posts' && !parentID) {
        const userPostCount = allPosts().filter(
          (p) => p.user_record_id === userRecord.id,
        ).length;
        // Just inserted = first ever if pre-insert count was zero. Note
        // that allPosts() may not have the new row yet, so 0 means this
        // was the first post.
        if (userPostCount === 0) {
          window.dispatchEvent(new CustomEvent('wipe-first-post'));
        }
      }
      onSubmit && onSubmit();
    } catch (error) {
      console.error('Error submitting:', error);
      setErrorMsg('Failed to submit. Please try again.');
      hapticError();
      playError();
    }
  };

  const toggleClock = () => {
    setShowClock((prev) => !prev);
  };

  const timeFromSettings = (type: 'posts' | 'votes' | 'follows'): TimeSelection => {
    const s = userRecord?.user_settings;
    if (type === 'posts') return { days: s?.postDays ?? 1, hours: s?.postHours ?? 0, minutes: s?.postMinutes ?? 0 };
    if (type === 'votes') return { days: s?.voteDays ?? 0, hours: s?.voteHours ?? 1, minutes: s?.voteMinutes ?? 0 };
    return { days: s?.followDays ?? 5, hours: s?.followHours ?? 0, minutes: s?.followMinutes ?? 0 };
  };

  const openTimePicker = () => {
    if (objectType === 'posts' || objectType === 'votes' || objectType === 'follows') {
      setTime(timeFromSettings(objectType));
    }
    setShowClock(true);
  };

  useEffect(() => {
    if (objectType === 'posts' || objectType === 'votes' || objectType === 'follows') {
      setTime(timeFromSettings(objectType));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectType, userRecord]);

  return (
    <>
    <Snackbar
      open={!!errorMsg}
      autoHideDuration={4000}
      onClose={() => setErrorMsg(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert severity="error" onClose={() => setErrorMsg(null)} sx={{ width: '100%' }}>
        {errorMsg}
      </Alert>
    </Snackbar>
    <Snackbar
      open={!!successMsg}
      autoHideDuration={3000}
      onClose={() => setSuccessMsg(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert severity="success" onClose={() => setSuccessMsg(null)} sx={{ width: '100%' }}>
        {successMsg}
      </Alert>
    </Snackbar>
    <Box ref={ref} style={{ display: 'flex', justifyContent: 'flex-end' }}>
      {!userRecord ? (
        'Sign in to ' + creationType
      ) : (
        <Stack direction="row" spacing={1.5} alignItems="center">
          {/* Time cost label shown when there's content */}
          {value.length > 0 && !showClock && (() => {
            const baseLabel = `${time.days > 0 ? `${time.days}d ` : ''}${time.hours > 0 ? `${time.hours}h ` : ''}${time.minutes > 0 ? `${time.minutes}m` : time.days === 0 && time.hours === 0 ? '0m' : ''}`;
            const powerActive = getPowerHourStatus().isActive;
            if (!powerActive) {
              return (
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: '"VT323", monospace', fontSize: '1rem', letterSpacing: 0.5 }}>
                  -{baseLabel}
                </Typography>
              );
            }
            // Power Hour: show discounted wallet cost next to strikethrough full cost
            const fullSeconds = time.days * 86400 + time.hours * 3600 + time.minutes * 60;
            const halfSeconds = Math.round(fullSeconds / 2);
            const halfH = Math.floor(halfSeconds / 3600);
            const halfM = Math.floor((halfSeconds % 3600) / 60);
            const halfLabel = `${halfH > 0 ? `${halfH}h ` : ''}${halfM > 0 ? `${halfM}m` : halfH === 0 ? `${Math.floor(halfSeconds)}s` : ''}`;
            return (
              <Typography variant="caption" sx={{ fontFamily: '"VT323", monospace', fontSize: '1rem', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box component="span" sx={{ textDecoration: 'line-through', color: 'text.disabled' }}>-{baseLabel}</Box>
                <Box component="span" sx={{ color: '#ff5722', fontWeight: 700 }}>⚡-{halfLabel}</Box>
              </Typography>
            );
          })()}
          {value.length === 0 || showClock ? (
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" disabled size="small">
                <PermanenceIcon style={{ width: 14, height: 14 }} />
              </Button>
              <Button variant="contained" disabled size="small">
                {creationType}
              </Button>
            </Stack>
          ) : (
            <Stack direction="row" spacing={1}>
              <Tooltip title={`Make permanent (uses 1 permanence token, never expires)`}>
                <Button variant="outlined" size="small" onClick={() => handleSubmit(true)}>
                  <PermanenceIcon style={{ width: 14, height: 14 }} />
                </Button>
              </Tooltip>

              <Tooltip title="Click to submit — long press to change duration · ⌘↵ quick submit">
                <Button
                  variant="contained"
                  size="small"
                  onClick={openTimePicker}
                  data-composer-primary-submit
                >
                  {creationType}
                </Button>
              </Tooltip>
            </Stack>
          )}
          <TimePicker
            time={time}
            setTime={setTime}
            submit={handleSubmit}
            showClock={showClock}
            toggleClock={toggleClock}
            parentRef={ref}
          />
        </Stack>
      )}
    </Box>
    </>
  );
}

export default SubmitWithTimeButton;
